import { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import {
  Avatar,
  Box,
  Button,
  Divider,
  Hidden,
  lighten,
  List,
  ListItem,
  ListItemText,
  Popover,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ExpandMoreTwoToneIcon from '@mui/icons-material/ExpandMoreTwoTone';
import AccountTreeTwoToneIcon from '@mui/icons-material/AccountTreeTwoTone';
import LogoutIcon from '@mui/icons-material/Logout';
import { useDispatch, useSelector } from 'react-redux';
import { getProfile, logout } from 'src/redux/authSlice';
import api from 'src/redux/config';
import { useTranslation } from 'react-i18next';

/* ===================== STYLES ===================== */

const UserBoxButton = styled(Button)(({ theme }) => ({
  paddingInline: theme.spacing(1),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));

const MenuUserBox = styled(Box)(({ theme }) => ({
  background: theme.colors.alpha.black[5],
  padding: theme.spacing(2),
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1)
}));

const UserBoxText = styled(Box)(() => ({
  textAlign: 'start'
}));

const UserBoxLabel = styled(Typography)(({ theme }) => ({
  fontWeight: theme.typography.fontWeightBold,
  color: theme.palette.secondary.main,
  display: 'block'
}));

const UserBoxDescription = styled(Typography)(({ theme }) => ({
  color: lighten(theme.palette.secondary.main, 0.5)
}));

/* ===================== COMPONENT ===================== */

function HeaderUserbox() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const authUser = useSelector((x) => x.user);
  const { t, i18n } = useTranslation();

  const isArabic = i18n.dir() === 'rtl'; // ✅ ONLY ARABIC

  const [imageData, setImageData] = useState(null);
  const [isOpen, setOpen] = useState(false);
  const ref = useRef(null);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/profile');
  };

  const user = {
    firstname: authUser?.profile?.firstname || '',
    lastname: authUser?.profile?.lastname || '',
    jobtitle: authUser?.profile?.userKind || ''
  };

  useEffect(() => {
    dispatch(getProfile());
  }, [dispatch]);

  useEffect(() => {
    let isMounted = true;

    api
      .get('/user/profile-picture', {
        headers: {
          Authorization: authUser?.accessToken
        },
        responseType: 'arraybuffer'
      })
      .then((response) => {
        if (isMounted) {
          const blob = new Blob([new Uint8Array(response.data)], {
            type: 'image/jpeg'
          });
          setImageData(URL.createObjectURL(blob));
        }
      })
      .catch(() => {});

    return () => {
      isMounted = false;
    };
  }, [authUser?.accessToken]);

  return (
    <>
      {/* ================= BUTTON ================= */}
      <UserBoxButton
        color="secondary"
        ref={ref}
        onClick={() => setOpen(true)}
        sx={{
          flexDirection: isArabic ? 'row-reverse' : 'row'
        }}
      >
        <Avatar
          variant="rounded"
          src={imageData || require('../../../../assets/images/profile.png')}
          sx={{ width: 30, height: 30, borderRadius: '50%' }}
        />

        <Hidden mdDown>
          <UserBoxText>
            <UserBoxLabel variant="body1">
              {user.firstname} {user.lastname}
            </UserBoxLabel>
            <UserBoxDescription variant="body2">
              {t(`accountType.${user.jobtitle}`)}
            </UserBoxDescription>
          </UserBoxText>
        </Hidden>

        <Hidden smDown>
          <ExpandMoreTwoToneIcon />
        </Hidden>
      </UserBoxButton>

      {/* ================= POPOVER ================= */}
      <Popover
        anchorEl={ref.current}
        open={isOpen}
        onClose={() => setOpen(false)}
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right'
        }}
      >
        <MenuUserBox
          sx={{
            minWidth: 220,
            flexDirection: isArabic ? 'row-reverse' : 'row'
          }}
        >
          <Avatar
            variant="rounded"
            src={imageData}
            sx={{ borderRadius: '50%' }}
          />
          <UserBoxText>
            <UserBoxLabel variant="body1">
              {user.firstname} {user.lastname}
            </UserBoxLabel>
          </UserBoxText>
        </MenuUserBox>

        <Divider />

        {/* ================= MENU ================= */}
        <List sx={{ p: 1 }}>
          <ListItem
            button
            component={NavLink}
            to="/management/profile/settings"
            onClick={() => setOpen(false)}
            sx={{
              display: 'flex',
              flexDirection: isArabic ? 'row-reverse' : 'row',
              gap: 1,
              textAlign: isArabic ? 'right' : 'left'
            }}
          >
            <AccountTreeTwoToneIcon />
            <ListItemText primary={t('Profile')} />
          </ListItem>

          <ListItem
            button
            component={NavLink}
            to="/management/company/settings"
            onClick={() => setOpen(false)}
            sx={{
              display: 'flex',
              flexDirection: isArabic ? 'row-reverse' : 'row',
              gap: 1,
              textAlign: isArabic ? 'right' : 'left'
            }}
          >
            <AccountTreeTwoToneIcon />
            <ListItemText primary={t('Company Settings')} />
          </ListItem>
        </List>

        <Divider />

        {/* ================= LOGOUT ================= */}
        <Box sx={{ m: 1 }}>
          <Button
            fullWidth
            color="primary"
            onClick={handleLogout}
            sx={{
              display: 'flex',
              flexDirection: isArabic ? 'row-reverse' : 'row',
              gap: 1
            }}
          >
            <LogoutIcon />
            {t('Exit')}
          </Button>
        </Box>
      </Popover>
    </>
  );
}

export default HeaderUserbox;
